/**
 * Zod schemas for the five corpus shapes and for user-data import — §4.2.
 *
 * Run in an INTEGRITY TEST over the real files, and on USER-DATA IMPORT. NOT on every
 * page load: validating 84k words per navigation costs more than it catches for an
 * artefact we produce ourselves.
 *
 * The corpus schemas are deliberately LOOSE about unknown keys — a future build that
 * adds a field must not make every shipped page fail validation.
 */
import { z } from 'zod'
import { TOTAL_PAGES } from './constants'

const page = z.number().int().min(1).max(TOTAL_PAGES)
const verseKeyString = z.string().regex(/^\d{1,3}:\d{1,3}$/)

/** [text, ...ruleIds] — the first element is the text, the rest are rule ids. */
export const tajweedRunSchema = z.tuple([z.string()]).rest(z.string())

// ---------------------------------------------------------------------------
// meta.json
// ---------------------------------------------------------------------------

export const metaSchema = z.looseObject({
  totalPages: z.literal(TOTAL_PAGES),
  bismillahFont: z.string(),
  books: z.record(
    z.string(),
    z.looseObject({ key: z.string(), name: z.string(), fullName: z.string() }),
  ),
  reciters: z
    .array(
      z.looseObject({
        id: z.string(),
        name: z.string(),
        style: z.string(),
        pace: z.number().positive(),
      }),
    )
    .min(1),
  tajweedRules: z.record(z.string(), z.string()),
  posLabels: z.record(z.string(), z.string()),
  featureLabels: z.record(z.string(), z.string()),
  chapters: z
    .array(
      z.looseObject({
        id: z.number().int().min(1).max(114),
        name: z.string(),
        nameArabic: z.string(),
        translatedName: z.string(),
        revelationPlace: z.string(),
        versesCount: z.number().int().positive(),
        pages: z.tuple([page, page]),
        bismillahPre: z.boolean(),
      }),
    )
    .length(114),
  juzStartPage: z.record(z.string(), page),
  pages: z
    .array(
      z.looseObject({
        page,
        font: z.string(),
        juz: z.number().int().min(1).max(30),
        surahs: z.array(z.number().int().min(1).max(114)),
        first: verseKeyString,
        last: verseKeyString,
      }),
    )
    .length(TOTAL_PAGES),
})

// ---------------------------------------------------------------------------
// mushaf/NNN.json
// ---------------------------------------------------------------------------

export const mushafWordSchema = z.looseObject({
  code: z.number().int(),
  char: z.string(),
  font: z.string(),
  text: z.string(),
  type: z.enum(['surah_header', 'bismillah', 'word', 'end', 'quarter']),
  sura: z.number().int().min(1).max(114).optional(),
  verse_key: verseKeyString.optional(),
  /**
   * NULLABLE, not merely optional — a rubʿ marker sits between words and belongs to
   * no position. All 199 quarter markers carry null.
   */
  position: z.number().int().positive().nullable().optional(),
  v4: z.string().optional(),
  tj: z.array(tajweedRunSchema).optional(),
})

export const mushafPageSchema = z.looseObject({
  page,
  font: z.string(),
  surahs: z.array(
    z.looseObject({
      id: z.number().int().min(1).max(114),
      name: z.string(),
      name_arabic: z.string(),
      verse_start: z.number().int().positive(),
      verse_end: z.number().int().positive(),
    }),
  ),
  lines: z
    .array(z.looseObject({ line: z.number().int().positive(), words: z.array(mushafWordSchema) }))
    .min(1),
})

// ---------------------------------------------------------------------------
// tafsir/NNN.json
// ---------------------------------------------------------------------------

/** [form, pos, root|null, lemma|null, ...features] — length 4–10. */
export const morphologySegmentSchema = z
  .tuple([z.string(), z.string(), z.string().nullable(), z.string().nullable()])
  .rest(z.string())

export const tafsirAyahSchema = z.looseObject({
  // Optional books are BLANK ROWS IN THE UPSTREAM DATABASE, not a grouping artefact.
  // Report the gap, do not hide it.
  k: z.string().optional(),
  b: z.string(),
  tb: z.string().optional(),
  q: z.string().optional(),
  s: z.string().optional(),
  a: z.string().optional(),
  w: z.string(),
  i: z.string().optional(),
  t: z.array(tajweedRunSchema),
  m: z.record(z.string(), z.array(morphologySegmentSchema)),
})

export const tafsirPageSchema = z.looseObject({
  page,
  ayat: z.record(verseKeyString, tafsirAyahSchema),
})

// ---------------------------------------------------------------------------
// search.json
// ---------------------------------------------------------------------------

export const searchRowSchema = z.looseObject({
  k: verseKeyString,
  p: page,
  t: z.string().min(1),
  n: z.string(),
  s: z.string(),
})

export const searchIndexSchema = z.array(searchRowSchema).length(6236)

// ---------------------------------------------------------------------------
// audio/<reciterId>.json
// ---------------------------------------------------------------------------

export const surahAudioSchema = z.looseObject({
  url: z.string().url(),
  dur: z.number().nonnegative(),
  /** Boundaries, not durations: always versesCount + 1 long. */
  t: z.array(z.number().nonnegative()).min(2),
})

export const reciterAudioSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  style: z.string(),
  surahs: z.record(z.string(), surahAudioSchema),
})
